/**
 * Utilitários de formatação de números para o padrão brasileiro
 *
 * Padrão:
 * - Ponto (.) para separador de milhares
 * - Vírgula (,) para separador decimal
 * - Sempre duas casas decimais para valores monetários e percentuais
 * - Sem dízimas periódicas ou arredondamentos incorretos
 */

/**
 * Trunca um número para N casas decimais SEM arredondar
 * Evita dízimas periódicas e arredondamentos
 */
function truncateDecimals(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals);
  return Math.trunc(value * factor) / factor;
}

/**
 * Formata um número inteiro no padrão brasileiro
 * Ex: 1234567 -> "1.234.567"
 */
export function formatInteger(
  value: number | string | null | undefined,
): string {
  if (value === null || value === undefined || value === "") return "0";

  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "0";

  // Trunca para inteiro (sem arredondar)
  const intValue = Math.trunc(num);

  return intValue.toLocaleString("pt-BR", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
}

/**
 * Formata um valor monetário no padrão brasileiro
 * Ex: 1234.56 -> "R$ 1.234,56"
 *
 * @param value - Valor numérico
 * @param showSymbol - Se deve exibir o símbolo R$ (padrão: true)
 */
export function formatCurrency(
  value: number | string | null | undefined,
  showSymbol: boolean = true,
): string {
  if (value === null || value === undefined || value === "") {
    return showSymbol ? "R$ 0,00" : "0,00";
  }

  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) {
    return showSymbol ? "R$ 0,00" : "0,00";
  }

  // Trunca para 2 casas decimais SEM arredondar
  const truncated = truncateDecimals(num, 2);

  const formatted = truncated.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return showSymbol ? `R$ ${formatted}` : formatted;
}

/**
 * Formata um valor percentual no padrão brasileiro
 * Ex: 12.345 -> "12,34%"
 *
 * @param value - Valor numérico (já em percentual, não decimal)
 * @param showSymbol - Se deve exibir o símbolo % (padrão: true)
 */
export function formatPercent(
  value: number | string | null | undefined,
  showSymbol: boolean = true,
): string {
  if (value === null || value === undefined || value === "") {
    return showSymbol ? "0,00%" : "0,00";
  }

  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) {
    return showSymbol ? "0,00%" : "0,00";
  }

  // Trunca para 2 casas decimais SEM arredondar
  const truncated = truncateDecimals(num, 2);

  const formatted = truncated.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return showSymbol ? `${formatted}%` : formatted;
}

/**
 * Formata um número decimal genérico no padrão brasileiro
 * Ex: 1234.567 -> "1.234,56" (com 2 casas decimais)
 *
 * @param value - Valor numérico
 * @param decimals - Número de casas decimais (padrão: 2)
 */
export function formatDecimal(
  value: number | string | null | undefined,
  decimals: number = 2,
): string {
  if (value === null || value === undefined || value === "") return "0,00";

  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "0,00";

  // Trunca para N casas decimais SEM arredondar
  const truncated = truncateDecimals(num, decimals);

  return truncated.toLocaleString("pt-BR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Formata um número baseado no tipo de dado
 * Útil para formatação dinâmica em tabelas
 *
 * @param value - Valor numérico
 * @param dataType - Tipo do dado: 'NUMBER', 'CURRENCY', 'PERCENT', 'DECIMAL'
 */
function formatByType(
  value: number | string | null | undefined,
  dataType: "NUMBER" | "CURRENCY" | "PERCENT" | "DECIMAL" | string,
): string {
  switch (dataType?.toUpperCase()) {
    case "CURRENCY":
      return formatCurrency(value);
    case "PERCENT":
      return formatPercent(value);
    case "DECIMAL":
      return formatDecimal(value);
    case "NUMBER":
    default:
      return formatInteger(value);
  }
}

/**
 * Formata número compacto para exibição em cards
 * Ex: 1234567 -> "1,23M", 12345 -> "12,3K"
 *
 * @param value - Valor numérico
 * @param dataType - Tipo do dado para prefixo/sufixo apropriado
 */
export function formatCompact(
  value: number | string | null | undefined,
  dataType?: "NUMBER" | "CURRENCY" | "PERCENT" | string,
): string {
  if (value === null || value === undefined || value === "") return "0";

  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "0";

  const absNum = Math.abs(num);
  const sign = num < 0 ? "-" : "";

  let formatted: string;

  if (absNum >= 1_000_000_000) {
    formatted = formatDecimal(absNum / 1_000_000_000, 2) + "B";
  } else if (absNum >= 1_000_000) {
    formatted = formatDecimal(absNum / 1_000_000, 2) + "M";
  } else if (absNum >= 1_000) {
    formatted = formatDecimal(absNum / 1_000, 1) + "K";
  } else if (
    dataType?.toUpperCase() === "CURRENCY" ||
    dataType?.toUpperCase() === "PERCENT"
  ) {
    formatted = formatDecimal(absNum, 2);
  } else {
    formatted = formatInteger(absNum);
  }

  const prefix = dataType?.toUpperCase() === "CURRENCY" ? "R$ " : "";
  const suffix = dataType?.toUpperCase() === "PERCENT" ? "%" : "";

  return `${prefix}${sign}${formatted}${suffix}`;
}

/**
 * Formata a variação percentual de uma comparação **com o sinal explícito no número**.
 *
 * Ex.: 12.5 -> "+12,50%", -5.3 -> "-5,30%", 0 -> "0,00%"
 *
 * O item 34 da auditoria da Magnata: o percentual de comparação saía em módulo, e a direção
 * ficava por conta da cor e da seta. Isso some na impressão em preto e branco, na leitura por
 * quem não distingue vermelho de verde e em qualquer cópia do valor para fora da tela — um
 * "12,50%" colado numa planilha vira um aumento, mesmo tendo sido uma queda. O sinal passa a
 * viver no número; a cor e a seta continuam, agora como reforço e não como a única informação.
 *
 * Duas escolhas que este formatador faz **diferente** do resto do módulo, e de propósito:
 *
 * - **O menos é o do teclado (U+002D), e não o tipográfico.** O `−` alinha melhor com o `+`,
 *   mas não é um menos numérico para planilha nenhuma: colado no Excel ou no Sheets, o valor
 *   vira célula de texto — o que derruba justamente o motivo de pôr o sinal ali. É também o
 *   sinal que `formatCurrency` e `formatDecimal` já emitem, via `toLocaleString`, de modo que
 *   uma linha de tabela não mistura dois traços diferentes.
 * - **Arredonda, em vez de truncar.** Truncar existe neste módulo para que valor monetário
 *   nunca saia maior do que é. Numa variação isso não paga: com zero casas, `0,9%` viraria
 *   `0%` e `12,7%` viraria `12%` — e todas as implementações que este formatador substituiu
 *   arredondavam. `signDisplay: "exceptZero"` decide o sinal **depois** do arredondamento, o
 *   que evita o `-0,0%` que uma variação de `-0,04` produziria.
 *
 * @param value - a variação já em pontos percentuais (12.5 é "12,5%", não "1250%")
 * @param decimals - casas decimais; 1 nas tabelas densas, 2 nos cards (padrão)
 */
export function formatSignedPercent(
  value: number | string | null | undefined,
  decimals: number = 2,
): string {
  if (value === null || value === undefined || value === "") return "—";

  const num = typeof value === "string" ? parseFloat(value) : value;
  if (!Number.isFinite(num)) return "—";

  const formatted = num.toLocaleString("pt-BR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    signDisplay: "exceptZero",
  });

  return `${formatted}%`;
}

/**
 * Retorna a classe CSS para colorir baseado no valor
 */
function getChangeColorClass(
  value: number | string | null | undefined,
): string {
  if (value === null || value === undefined || value === "")
    return "text-muted-foreground";

  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "text-muted-foreground";

  if (num > 0) return "text-green-600 dark:text-green-400";
  if (num < 0) return "text-red-600 dark:text-red-400";
  return "text-muted-foreground";
}

/**
 * Formata ROAS (Return on Ad Spend)
 * Ex: 3.456 -> "3,45x"
 */
function formatROAS(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "0,00x";

  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "0,00x";

  const truncated = truncateDecimals(num, 2);

  return (
    truncated.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + "x"
  );
}

/**
 * Formata CPC, CPM, CPA (valores de custo)
 * Ex: 1.234 -> "R$ 1,23"
 */
function formatCost(value: number | string | null | undefined): string {
  return formatCurrency(value);
}

/**
 * Formata CTR (Click Through Rate)
 * Ex: 2.345 -> "2,34%"
 */
function formatCTR(value: number | string | null | undefined): string {
  return formatPercent(value);
}

// Export default object com todas as funções
